import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Config } from 'config/envConfig';
import axios from 'axios';
import { AlertType, alert } from '@quyva/shared-ui';
import { base64ToBlob, validateWebsiteUrl } from '@quyva/common';
import cloneDeep from 'lodash/cloneDeep';
import { getUrl } from '../components/pdfDisplay/utils';
import { Operation } from '@quyva/common';
import { isValidAzureTenant } from '../utils/validateTenantId';

const SettingsContext = createContext(null);

const getLogoFingerprint = (logo) => {
    if (!logo) return null;
    if (logo instanceof File) {
        return {
            kind: 'file',
            name: logo.name,
            size: logo.size,
            type: logo.type,
            lastModified: logo.lastModified,
        };
    }
    if (logo instanceof Blob) {
        return {
            kind: 'blob',
            size: logo.size,
            type: logo.type,
        };
    }
    return { kind: typeof logo };
};

const fetchAccountDetails = async (user) => {
    try {
        const encodedToken = encodeURIComponent(user.token);
        const orgDetailsUrl = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/details`
        );

        const orgResp = await axios.get(orgDetailsUrl, {
            headers: {
                Authorization: encodedToken,
            },
        });

        const org = orgResp?.data?.details || {};
        const logoBlob = org.logo ? base64ToBlob(org.logo, 'image/png') : null;

        return {
            personalDetails: {
                email: user.email,
                username: user.username || '',
                currency: user.currency || '',
            },
            vendorDetails: {
                company_name: org.name || '',
                company_domain: org.website || '',
                copy_right_text: org.copy_right_text || '',
                logo: logoBlob,
            },
        };
    } catch (err) {
        if (err?.response?.status === 404) {
            return {
                personalDetails: {
                    email: user.email,
                    username: user.username || '',
                    currency: user.currency || '',
                },
                vendorDetails: {
                    company_name: '',
                    company_domain: '',
                    copy_right_text: '',
                    logo: null,
                },
            };
        }
        alert('Failed to get account details: ', err.message);
    }
};

const updateVendorDetails = async (user, accountDetails) => {
    const vendorDetails = accountDetails.vendorDetails;
    if (!vendorDetails) return;

    const formData = new FormData();
    formData.append('name', vendorDetails.company_name || '');
    formData.append('website', vendorDetails.company_domain || '');
    formData.append('copy_right_text', vendorDetails.copy_right_text);

    if (vendorDetails.logo) {
        const logo = vendorDetails.logo;
        if (logo instanceof File) {
            formData.append('logo', logo);
        } else {
            const fileBlob = new Blob([logo], {
                type: logo?.type || 'application/octet-stream',
            });
            formData.append('logo', fileBlob, 'logo');
        }
    } else {
        formData.append('logo', '');
    }

    const encodedToken = encodeURIComponent(user.token);
    const orgUpdateUrl = getUrl(
        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/update`
    );

    await axios.put(orgUpdateUrl, formData, {
        headers: {
            Authorization: encodedToken,
        },
    });
};

const normalizeProcessName = (name) =>
    String(name || '')
        .trim()
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ');

const validatePassword = (password) => {
    const minLength = 8;
    const isValid =
        password &&
        password.length >= minLength &&
        /[A-Z]/.test(password) &&
        /[a-z]/.test(password) &&
        /\d/.test(password) &&
        /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return isValid
        ? ''
        : 'Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a digit, and a special character.';
};

export const SettingsProvider = ({ children, isAdmin }) => {
    const user = useMemo(() => JSON.parse(localStorage.getItem('current')), []);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // General Section States
    const [historyCounts, setHistoryCounts] = useState(5);
    const [originalHistoryCounts, setOriginalHistoryCounts] = useState(5);
    const [refine, setRefine] = useState(false);
    const [originalRefine, setOriginalRefine] = useState(null);

    // Organization Section States
    const [organizationId, setOrganizationId] = useState(null);
    const [organizationName, setOrganizationName] = useState(null);
    const [organizationAddress, setOrganizationAddress] = useState(null);
    const [organizationIndustry, setOrganizationIndustry] = useState(null);
    const [organizationWebsite, setOrganizationWebsite] = useState(null);
    const [accountDetails, setAccountDetails] = useState({});
    const [originalAccountDetails, setOriginalAccountDetails] = useState(null);
    const [teamsEnabled, setTeamsEnabled] = useState(false);
    const [organizationTenantId, setOrganizationTenantId] = useState(null);
    const [teamsGreeting, setTeamsGreeting] = useState('');
    // null = "not fetched yet" -- distinct from '' (a legitimately empty/LLM-generated
    // greeting), so the dirty-check below never fires before the greeting has loaded.
    const [originalTeamsGreeting, setOriginalTeamsGreeting] = useState(null);

    // Organization Management States
    const [organizationOwner, setOrganizationOwner] = useState(null);
    const [orgQueryLimit, setOrgQueryLimit] = useState(null);
    const [isPostpaid, setIsPostpaid] = useState(false);
    const [orgCurrency, setOrgCurrency] = useState('INR');
    const [orgPerUserCost, setOrgPerUserCost] = useState(100);
    const [originalOrganizationSnapshot, setOriginalOrganizationSnapshot] =
        useState(null);

    const [userRole, setUserRole] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isPartnerOwner, setIsPartnerOwner] = useState(false);
    const [isReseller, setIsReseller] = useState(false);
    const [supportedCurrencies, setSupportedCurrencies] = useState([]);

    // Account Section States
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [processes, setProcesses] = useState([]);
    const [originalProcesses, setOriginalProcesses] = useState([]);
    const [selectedProcess, setSelectedProcess] = useState(null);

    // System Settings States
    const [isUserKey, setIsUserKey] = useState(false);
    const [isDbOnly, setIsDbOnly] = useState(false);
    const [systemKeysByProvider, setSystemKeysByProvider] = useState({});
    const [originalSystemKeysByProvider, setOriginalSystemKeysByProvider] = useState({});

    const hasMountedRef = useRef(false);

    const fetchOrganizationDetails = async () => {
        try {
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/details`
            );
            const authToken = encodeURIComponent(user.token);

            const response = await axios.get(url, {
                headers: {
                    Authorization: authToken,
                },
            });

            const data = response.data;

            setOrganizationId(data.details.id);
            setOrganizationName(data.details.name);
            setOrganizationAddress(data.details.address);
            setOrganizationIndustry(data.details.industry);
            setOrganizationWebsite(data.details.website);
            setOrganizationOwner(data.details.owner);
            setUserRole(data.details.userRole);
            setOrgQueryLimit(data.details.org_wide_query_limit);
            setIsPostpaid(data.details.is_postpaid);
            setOrgCurrency(data.details.currency || 'INR');
            setOrgPerUserCost(data.details.per_user_cost ?? 100);
            setTeamsEnabled(data.details.teamsEnabled);
            setOrganizationTenantId(data.details.tenantId);
        } catch (err) {
            if (err?.response?.status === 404) {
                return;
            }
            alert(
                AlertType.ERROR,
                'Failed to fetch organization details: ' + err.message
            );
        }
    };

    // Loads the Teams greeting alongside the rest of the org section so it
    // participates in the same dirty-tracking/save flow as everything else,
    // instead of needing its own separate save button.
    useEffect(() => {
        if (!teamsEnabled || !organizationId || !user?.token) return;
        const fetchTeamsGreeting = async () => {
            try {
                const url = getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/teams/config/${organizationId}`
                );
                const encodedToken = encodeURIComponent(user.token);
                const resp = await axios.get(url, {
                    headers: { Authorization: encodedToken },
                });
                const gm = resp?.data?.data?.greetingMessage;
                setTeamsGreeting(gm ?? '');
                setOriginalTeamsGreeting(gm ?? '');
            } catch (_) {
                // non-critical — leave empty
            }
        };
        fetchTeamsGreeting();
    }, [teamsEnabled, organizationId, user?.token]);

    const fetchUserRoleFlags = async () => {
        try {
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}`
            );
            const authToken = encodeURIComponent(user.token);

            const response = await axios.get(
                `${url}/check-user-role?email=${encodeURIComponent(user.email)}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: authToken,
                    },
                }
            );

            setIsSuperAdmin(!!response.data?.isSuperAdmin);
            setIsPartnerOwner(!!response.data?.isPartnerOwner);
            setIsReseller(!!response.data?.isReseller);
        } catch (err) {
            console.error('Error checking user role:', err);
        }
    };

    const getHistoryLimit = async () => {
        const apiUrl = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/history/getHistoryLimit?email=${encodeURIComponent(
                user.email
            )}`
        );
        const response = await axios.get(apiUrl);
        const limit = response.data.history_limit;
        const safeLimit = Math.max(5, limit || 5);
        setHistoryCounts(safeLimit);
        setOriginalHistoryCounts(safeLimit);
    };

    const fetchRefineData = async () => {
        const response = await axios.get(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/check_refine/?email=${encodeURIComponent(
                    user.email
                )}`
            )
        );
        setRefine(!!response.data?.refine);
    };

    const loadAccountSection = async () => {
        const accountData = await fetchAccountDetails(user);
        if (accountData) {
            setAccountDetails(accountData);
            setOriginalAccountDetails(structuredClone(accountData));
        }
    };

    const loadGeneralSection = async () => {
        await Promise.allSettled([fetchRefineData(), getHistoryLimit()]);
    };

    const loadOrganizationSection = async () => {
        await fetchOrganizationDetails();
    };

    const loadPromptSection = async () => {
        await fetchProcesses();
    };

    const fetchProcesses = async () => {
        if (!user?.email) return;
        if (!isAdmin) {
            setProcesses([]);
            setOriginalProcesses([]);
            return;
        }

        const res = await axios.get(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/prompts_routes/fetch-system-prompts?email=${encodeURIComponent(
                    user.email
                )}`
            )
        );
        const data = res.data;

        const nextProcesses = (data || []).filter(
            (p) =>
                normalizeProcessName(p?.process_name) !==
                normalizeProcessName(Operation.PERSONA_PROMPTS)
        );

        setProcesses(nextProcesses);
        setOriginalProcesses(JSON.parse(JSON.stringify(nextProcesses)));

        const available = new Set(
            (nextProcesses || []).map((p) => p?.process_name).filter(Boolean)
        );
        if (selectedProcess && !available.has(selectedProcess)) {
            setSelectedProcess(null);
        }
    };

    useEffect(() => {
        if (!user?.email) return;
        fetchUserRoleFlags();
        setIsLoading(false);
    }, [user?.email]);

    useEffect(() => {
        axios.get(getUrl(`http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/exchange-rates`))
            .then(res => setSupportedCurrencies(res.data.supported || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (originalRefine === null) {
            setOriginalRefine(refine);
        }
    }, [refine, originalRefine]);

    useEffect(() => {
        if (originalOrganizationSnapshot) return;

        if (
            !organizationId ||
            !organizationName ||
            !accountDetails?.vendorDetails
        ) {
            return;
        }


        setOriginalOrganizationSnapshot({
            organizationId,
            organizationName,
            organizationAddress,
            organizationIndustry,
            organizationTenantId,
            teamsEnabled,
            organizationWebsite,
            orgQueryLimit,
            isPostpaid,
            userRole,
            orgCurrency,
            orgPerUserCost,
            copyRightText:
                accountDetails?.vendorDetails?.copy_right_text || '',
            logoBaseline: accountDetails?.vendorDetails?.logo || null,
            logoFingerprint: getLogoFingerprint(
                accountDetails?.vendorDetails?.logo
            ),
        });

    }, [
        originalOrganizationSnapshot,
        organizationId,
        organizationName,
        organizationAddress,
        organizationIndustry,
        organizationWebsite,
        orgQueryLimit,
        isPostpaid,
        userRole,
        accountDetails?.vendorDetails?.copy_right_text,
        accountDetails?.vendorDetails?.logo,
        teamsEnabled,
        organizationTenantId,
    ]);

    const isAccountDirty = useMemo(() => {
        if (!originalAccountDetails) return false;
        const cur = accountDetails?.personalDetails || {};
        const orig = originalAccountDetails?.personalDetails || {};
        return JSON.stringify(cur) !== JSON.stringify(orig);
    }, [accountDetails, originalAccountDetails]);

    const isGeneralDirty = useMemo(() => {
        const hChanged =
            originalHistoryCounts !== null &&
            historyCounts !== originalHistoryCounts;
        const rChanged = originalRefine !== null && refine !== originalRefine;
        return hChanged || rChanged;
    }, [historyCounts, originalHistoryCounts, refine, originalRefine]);

    const isOrganizationDirty = useMemo(() => {
        if (!originalOrganizationSnapshot) return false;
        if (organizationId !== originalOrganizationSnapshot.organizationId)
            return true;
        if (organizationName !== originalOrganizationSnapshot.organizationName)
            return true;
        if (
            organizationAddress !==
            originalOrganizationSnapshot.organizationAddress
        )
            return true;
        if (
            organizationIndustry !==
            originalOrganizationSnapshot.organizationIndustry
        )
            return true;
        if (
            organizationWebsite !==
            originalOrganizationSnapshot.organizationWebsite
        )
            return true;
        if (orgQueryLimit !== originalOrganizationSnapshot.orgQueryLimit)
            return true;
        if (orgCurrency !== originalOrganizationSnapshot.orgCurrency)
            return true;
        if (orgPerUserCost !== originalOrganizationSnapshot.orgPerUserCost)
            return true;
        const nextCopyRight =
            accountDetails?.vendorDetails?.copy_right_text || '';
        if (
            nextCopyRight !== (originalOrganizationSnapshot.copyRightText || '')
        )
            return true;

        const nextLogoFp = getLogoFingerprint(
            accountDetails?.vendorDetails?.logo
        );
        if (
            JSON.stringify(nextLogoFp) !==
            JSON.stringify(originalOrganizationSnapshot.logoFingerprint)
        ) {
            return true;
        }
        if (teamsEnabled !== originalOrganizationSnapshot.teamsEnabled)
            return true;
        if (
            organizationTenantId !==
            originalOrganizationSnapshot.organizationTenantId
        )
            return true;
        if (
            originalTeamsGreeting !== null &&
            teamsGreeting !== originalTeamsGreeting
        )
            return true;

        return false;
    }, [
        originalOrganizationSnapshot,
        organizationId,
        organizationName,
        organizationAddress,
        teamsGreeting,
        originalTeamsGreeting,
        organizationIndustry,
        organizationWebsite,
        teamsEnabled,
        organizationTenantId,
        orgQueryLimit,
        orgCurrency,
        orgPerUserCost,
        accountDetails?.vendorDetails?.copy_right_text,
        accountDetails?.vendorDetails?.logo,
    ]);

    const normalizeKeys = (obj = {}) => {
        return Object.entries(obj)
            .filter(([_, v]) => v && String(v).trim() !== '')
            .reduce((acc, [k, v]) => {
                acc[k] = String(v).trim();
                return acc;
            }, {});
    };

    const isSystemSettingsDirty = useMemo(() => {
        const current = normalizeKeys(systemKeysByProvider);
        const original = normalizeKeys(originalSystemKeysByProvider);

        return JSON.stringify(current) !== JSON.stringify(original);
    }, [systemKeysByProvider, originalSystemKeysByProvider]);

    const promptHasChanges = useMemo(() => {
        return JSON.stringify(processes) !== JSON.stringify(originalProcesses);
    }, [processes, originalProcesses]);

    const passwordDirty = !!newPassword || !!confirmPassword;

    const hasAnyUnsavedChanges =
        isAccountDirty ||
        passwordDirty ||
        isGeneralDirty ||
        isOrganizationDirty ||
        promptHasChanges ||
        isSystemSettingsDirty;

    const discardAllChanges = () => {
        if (originalAccountDetails) {
            setAccountDetails(cloneDeep(originalAccountDetails));
        }
        if (originalHistoryCounts !== null) {
            setHistoryCounts(originalHistoryCounts);
        }
        if (originalRefine !== null) {
            setRefine(originalRefine);
        }
        if (originalOrganizationSnapshot) {
            setOrganizationId(originalOrganizationSnapshot.organizationId);
            setOrganizationName(originalOrganizationSnapshot.organizationName);
            setOrganizationAddress(
                originalOrganizationSnapshot.organizationAddress
            );
            setOrganizationIndustry(
                originalOrganizationSnapshot.organizationIndustry
            );
            setTeamsEnabled(originalOrganizationSnapshot.teamsEnabled);
            setOrganizationTenantId(
                originalOrganizationSnapshot.organizationTenantId
            );
            setOrganizationWebsite(
                originalOrganizationSnapshot.organizationWebsite
            );
            setOrgQueryLimit(originalOrganizationSnapshot.orgQueryLimit);

            if (originalTeamsGreeting !== null) {
                setTeamsGreeting(originalTeamsGreeting);
            }

            setAccountDetails((prev) => {
                const next = { ...(prev || {}) };
                next.vendorDetails = { ...(next.vendorDetails || {}) };
                next.vendorDetails.copy_right_text =
                    originalOrganizationSnapshot.copyRightText || '';
                next.vendorDetails.logo =
                    originalOrganizationSnapshot.logoBaseline || null;
                return next;
            });
        }
        setProcesses(cloneDeep(originalProcesses));
        setNewPassword('');
        setConfirmPassword('');
        setSystemKeysByProvider(cloneDeep(originalSystemKeysByProvider || {}));
    };

    const handleInstructionChange = (idx, newValue) => {
        setProcesses((prev) =>
            prev.map((process) => {
                if (process.process_name === selectedProcess) {
                    return {
                        ...process,
                        sub_tasks: process.sub_tasks.map((t, i) =>
                            i === idx ? { ...t, instruction: newValue } : t
                        ),
                    };
                }
                return process;
            })
        );
    };

    const handlePromptNameChange = (idx, newValue) => {
        setProcesses((prev) =>
            prev.map((process) => {
                if (process.process_name === selectedProcess) {
                    return {
                        ...process,
                        sub_tasks: process.sub_tasks.map((t, i) =>
                            i === idx ? { ...t, prompt: newValue } : t
                        ),
                    };
                }
                return process;
            })
        );
    };

    const isFormValid = () => {
        if (organizationName != null && organizationName.trim() === '') {
            alert(AlertType.ERROR, 'Organization name cannot be empty');
            return false;
        }

        if (organizationWebsite && !validateWebsiteUrl(organizationWebsite)) {
            alert(
                AlertType.ERROR,
                'Please enter a valid website URL (e.g., https://example.com)'
            );
            return false;
        }
        if (
            teamsEnabled &&
            (!organizationTenantId || organizationTenantId.trim() === '')
        ) {
            alert(
                AlertType.ERROR,
                'Azure AD Tenant ID required if Microsoft Teams Access enabled'
            );
            return false;
        }

        if (
            organizationTenantId &&
            !isValidAzureTenant(organizationTenantId.trim())
        ) {
            alert(AlertType.ERROR, 'Please enter a valid Azure Tenant ID');
            return false;
        }

        return true;
    };

    const saveAccountSection = async () => {
        if (!isFormValid()) throw new Error();

        const encodedToken = encodeURIComponent(user.token);

        if (newPassword || confirmPassword) {
            if (newPassword !== confirmPassword) {
                alert(
                    AlertType.ERROR,
                    'New Password and Confirm Password do not match.'
                );
                return;
            }

            const validationError = validatePassword(newPassword);
            if (validationError) {
                alert(AlertType.ERROR, validationError);
                return;
            }

            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/update-password`
            );

            const requestBody = {
                email: user.email,
                newPassword: newPassword,
            };

            const response = await axios.post(dynamicUrl, requestBody, {
                headers: {
                    Authorization: encodedToken,
                },
            });

            if (response.data.message !== 'Password updated successfully!') {
                throw new Error(
                    response.data.message || 'Failed to update password'
                );
            }
        }

        if (isAccountDirty && organizationId) {
            const syncedAccountDetails = {
                ...accountDetails,
                vendorDetails: {
                    ...(accountDetails?.vendorDetails || {}),
                    company_name: organizationName || '',
                    company_domain: organizationWebsite || '',
                },
            };
            await updateVendorDetails(user, syncedAccountDetails);
        }

        if (isAccountDirty) {
            setOriginalAccountDetails(
                JSON.parse(JSON.stringify(accountDetails))
            );
        }
        setNewPassword('');
        setConfirmPassword('');
    };

    const updateHistoryLimit = async () => {
        const apiUrl = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/history/updateHistoryLimit`
        );

        const data = {
            email: user.email,
            history_limit: historyCounts,
        };

        await axios.post(apiUrl, data, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    };

    const handleRefineSubmit = async () => {
        await axios.get(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/process_data/?email=${encodeURIComponent(
                    user.email
                )}&refine=${refine}`
            )
        );
    };

    const saveGeneralSection = async () => {
        if (!isFormValid()) throw new Error();

        const hChanged =
            originalHistoryCounts !== null &&
            historyCounts !== originalHistoryCounts;
        const rChanged = originalRefine !== null && refine !== originalRefine;
        if (hChanged) {
            await updateHistoryLimit();
            setOriginalHistoryCounts(historyCounts);
        }
        if (rChanged) {
            await handleRefineSubmit();
            setOriginalRefine(refine);
        }
    };

    const handleUpdateOrganization = async () => {
        const authToken = encodeURIComponent(user.token);
        const url = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/update`
        );

        const formData = new FormData();
        if (organizationName !== undefined && organizationName !== null)
            formData.append('name', organizationName);
        if (organizationWebsite !== undefined && organizationWebsite !== null)
            formData.append('website', organizationWebsite);
        if (organizationAddress !== undefined && organizationAddress !== null)
            formData.append('address', organizationAddress);
        if (organizationIndustry !== undefined && organizationIndustry !== null)
            formData.append('industry', organizationIndustry);
        if (teamsEnabled !== undefined && teamsEnabled !== null)
            formData.append('teamsEnabled', teamsEnabled);
        if (organizationTenantId !== undefined && organizationTenantId !== null)
            formData.append('tenantId', organizationTenantId);
        if (orgQueryLimit !== undefined && orgQueryLimit !== null)
            formData.append('org_wide_query_limit', orgQueryLimit);
        if (orgCurrency !== undefined && orgCurrency !== null)
            formData.append('currency', orgCurrency);
        if (orgPerUserCost !== undefined && orgPerUserCost !== null)
            formData.append('per_user_cost', orgPerUserCost);

        const copyRightText = accountDetails?.vendorDetails?.copy_right_text;
        if (copyRightText !== undefined && copyRightText !== null) {
            formData.append('copy_right_text', copyRightText);
        }

        const logo = accountDetails?.vendorDetails?.logo;
        if (logo) {
            if (logo instanceof File) {
                formData.append('logo', logo);
            } else if (logo instanceof Blob) {
                formData.append('logo', logo, 'logo');
            } else {
                try {
                    const fileBlob = new Blob([logo], {
                        type: logo?.type || 'application/octet-stream',
                    });
                    formData.append('logo', fileBlob, 'logo');
                } catch (e) {
                    throw new Error('Failed to process logo file for upload');
                }
            }
        } else {
            formData.append('logo', '');
        }

        try {
            const response = await axios.put(url, formData, {
                headers: {
                    Authorization: authToken,
                },
            });
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to update organization details';

            throw new Error(message);
        }
    };

    const handleUpdateTeamsGreeting = async () => {
        if (!organizationId) return;
        const url = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/teams/greeting/${organizationId}`
        );
        const encodedToken = encodeURIComponent(user.token);
        try {
            await axios.put(
                url,
                // null = LLM-generated, "" = disabled, text = custom
                { greetingMessage: teamsGreeting === '' ? null : teamsGreeting },
                {
                    headers: {
                        Authorization: encodedToken,
                        'Content-Type': 'application/json',
                    },
                }
            );
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'Failed to save Teams greeting';
            throw new Error(message);
        }
    };

    const saveOrganizationSection = async () => {
        if (!isFormValid()) throw new Error();

        if (!isOrganizationDirty) return;
        if (organizationId && organizationName && userRole === 'admin') {
            await handleUpdateOrganization();
        }
        if (
            teamsEnabled &&
            originalTeamsGreeting !== null &&
            teamsGreeting !== originalTeamsGreeting
        ) {
            await handleUpdateTeamsGreeting();
            setOriginalTeamsGreeting(teamsGreeting);
        }

        setOriginalOrganizationSnapshot({
            organizationId,
            organizationName,
            organizationAddress,
            organizationIndustry,
            teamsEnabled,
            organizationTenantId,
            organizationWebsite,
            orgQueryLimit,
            orgCurrency,
            orgPerUserCost,
            isPostpaid,
            userRole,
            copyRightText: accountDetails?.vendorDetails?.copy_right_text || '',
            logoBaseline: accountDetails?.vendorDetails?.logo || null,
            logoFingerprint: getLogoFingerprint(
                accountDetails?.vendorDetails?.logo
            ),
        });
    };

    const savePromptSection = async () => {
        if (!promptHasChanges) return;

        let response;
        try {
            response = await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/prompts_routes/update-system-prompts`
                ),
                {
                    email: user.email,
                    processes,
                },
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );
        } catch (e) {
            const payload = e?.response?.data || {};
            throw new Error(
                payload?.detail || payload?.message || 'Failed to save prompts.'
            );
        }

        if (!response || response.status < 200 || response.status >= 300) {
            throw new Error('Failed to save prompts.');
        }

        setOriginalProcesses(JSON.parse(JSON.stringify(processes)));
    };

    const saveSystemSection = async () => {
        if (!isSuperAdmin) {
            throw new Error('Permission denied');
        }

        const url = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/license/set-user-key`
        );

        const entries = Object.entries(systemKeysByProvider || {})
            .map(([k, v]) => [String(k), String(v || '').trim()])
            .filter(([, v]) => !!v);

        if (entries.length === 0) {
            alert(AlertType.ERROR, 'Please enter at least one API key.');
            return;
        }

        for (const [compoundKey, secret] of entries) {
            const parts = compoundKey.split(':');
            const provider = parts[1] || '';
            if (!provider) continue;

            await axios.post(
                url,
                { provider, llm_key: secret },
                { headers: { 'Content-Type': 'application/json' } }
            );
        }

        setOriginalSystemKeysByProvider(
            JSON.parse(JSON.stringify(systemKeysByProvider || {}))
        );
    };

    const fetchUserKey = async () => {
        try {
            const response = await axios.get(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/is_user_key`
                ),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                }
            );

            const data = response.data;
            setIsUserKey(data.result);
            setIsDbOnly(data.primary_feature === 'FEATURE_DB');
        } catch (error) {
            console.error("Error fetching user key:", error);
        }
    };

    const fetchSystemKeys = async () => {
        try {
            const response = await axios.get(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/license/get-user-keys`
                ),
                { headers: { 'Content-Type': 'application/json' } }
            );
            const data = response.data;
            setSystemKeysByProvider(data);
            setOriginalSystemKeysByProvider(JSON.parse(JSON.stringify(data)));
        } catch (error) {
            console.error('Error fetching system keys:', error);
        }
    };

    useEffect(() => {
        fetchUserKey();
        fetchSystemKeys();
    }, []);

    const runWithSaving = async (fn) => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            await fn();
            alert(AlertType.SUCCESS, 'Save Successful');
        } catch (error) {
            alert(AlertType.ERROR, error.message || 'Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };

    const value = {
        user,
        isLoading,
        isSaving,
        setIsSaving,
        runWithSaving,
        isAdmin,
        accountDetails,
        setAccountDetails,
        historyCounts,
        setHistoryCounts,
        refine,
        setRefine,
        organizationId,
        organizationName,
        organizationAddress,
        organizationIndustry,
        teamsEnabled,
        setTeamsEnabled,
        organizationTenantId,
        setOrganizationTenantId,
        teamsGreeting,
        setTeamsGreeting,
        organizationWebsite,
        organizationOwner,
        userRole,
        isSuperAdmin,
        isPartnerOwner,
        isReseller,
        supportedCurrencies,
        orgQueryLimit,
        isPostpaid,
        orgCurrency,
        setOrgCurrency,
        orgPerUserCost,
        setOrgPerUserCost,
        setOrganizationName,
        setOrganizationAddress,
        setOrganizationIndustry,
        setOrganizationWebsite,
        setOrganizationOwner,
        setUserRole,
        setOrgQueryLimit,
        fetchOrganizationDetails,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        processes,
        setProcesses,
        originalProcesses,
        selectedProcess,
        setSelectedProcess,
        fetchProcesses,
        loadAccountSection,
        loadOrganizationSection,
        loadGeneralSection,
        loadPromptSection,
        handleInstructionChange,
        handlePromptNameChange,
        isAccountDirty,
        isGeneralDirty,
        isOrganizationDirty,
        promptHasChanges,
        passwordDirty,
        hasAnyUnsavedChanges,
        discardAllChanges,
        saveAccountSection,
        saveGeneralSection,
        saveOrganizationSection,
        savePromptSection,
        systemKeysByProvider,
        setSystemKeysByProvider,
        originalSystemKeysByProvider,
        isSystemSettingsDirty,
        saveSystemSection,
        isUserKey,
        setIsUserKey,
        fetchUserKey,
        isDbOnly,
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettingsContext = () => {
    const ctx = useContext(SettingsContext);
    if (!ctx) {
        throw new Error(
            'useSettingsContext must be used within SettingsProvider'
        );
    }
    return ctx;
};
